/*
 * flashmeeting-fe
 */

import { AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService, UserFilter, UserUmanforge, Users } from '@umanforge/common';
import { CommonUserService, store } from '@umanforge/common';
import { AxiosResponse, AxiosError } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';
import { API } from 'constants/api.constant';
import { GlobalService } from './global.service';
import { AxiosRequestConfig } from 'axios';

export interface NewUserPayload {
  firstname: string;
  lastname: string;
  email: string;
  clients?: { id: string; isAdmin?: boolean }[];
  sites?: { id: string; isAdmin?: boolean }[];
  sectors?: { id: string; isAdmin?: boolean }[];
  language?: string | null;
  timezone?: string | null;
  abilities?: { id: string; expirationDate?: string }[];
  skills?: { id: string; value?: number }[];
}

export class UsersService {

  /**
   * Post a new user to the backend
   */
  static postUser(
    body: NewUserPayload,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<{ id: string } | undefined> {
    return new Promise( async resolve => {
      AXIOS.post( API.USERS, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ id: string }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /**
   * Upload avatar for a user. Returns public avatar path or null
   */
  static async uploadAvatar(
    userId: string,
    clientId: string,
    file: File,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    try {
      const formData = new FormData();
      formData.append( 'content', file );
      const baseConfig: AxiosRequestConfig = await CommonGlobalService.getHeaderRequest( token );
      const headers = { ...( ( baseConfig && ( baseConfig.headers as Record<string, string> ) ) || {} ), 'Content-Type': 'multipart/form-data' } as Record<string, string>;
      const config: AxiosRequestConfig = { ...baseConfig, headers };
      const response = await AXIOS.post( API.USERS + `${userId}/avatar/${clientId}`, formData, config );
      CommonGlobalService.saveRequestToken( response, dispatch );
      if ( response.status === 200 ) {
        return response.data && response.data.avatar ? response.data.avatar as string : null;
      }
      onAxiosWarning( response );
      return null;
    } catch ( err ) {
      onAxiosError( err as AxiosError );
      return null;
    }
  }

  /**
   * Get avatar URLs for multiple users
   */
  static getUsersAvatars(
    usersId: string[], clientId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<{ userId: string, avatar: string | null }[] | null> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.USERS }avatars`, { ...await CommonGlobalService.getHeaderRequest( token ), params: { clientId, usersId } } )
        .then( ( response: AxiosResponse<{ avatars: { userId: string, avatar: string | null }[] }> ) => {
          const result: { userId: string, avatar: string | null }[] = [];
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          if ( response.status === 200 && response.data.avatars ) {
            result.push( ...UsersService.formattedUsersAvatarsResponse( response.data.avatars ) );
          }
          resolve( result );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getUserAvatar(
    userId: string, clientId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    return new Promise( resolve => {
      UsersService.getUsersAvatars( [ userId ], clientId, token, dispatch, onError, onWarning )
        .then( response => {
          let result: string | null = null;
          if ( response && response.length > 0 ) {
            result = response[ 0 ].avatar;
          }
          resolve( result );
        } );
    } );
  }

  static formattedUsersAvatarsResponse( avatars: { userId: string, avatar: string | null }[] ) {
    const axiosDefaults = AXIOS.defaults;
    const baseUrl = ( axiosDefaults && axiosDefaults.baseURL ) ? String( axiosDefaults.baseURL ).replace( /\/$/, '' ) : window.location.origin;
    return avatars.map( item => {
      if ( item.avatar && item.avatar.startsWith( '/' ) ) {
        return { userId: item.userId, avatar: `${baseUrl}${item.avatar}` };
      }
      return item;
    } );
  }

  static async getUser(
    userId: string,
    token: string | null = null, dispatch?: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UserUmanforge | null> {
    const tok = token ?? store.getState().token.value;
    const disp = dispatch ?? store.dispatch;
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, disp, onError ); };
    try {
      const config = { ...await CommonGlobalService.getHeaderRequest( tok ), params: { appName: 'FLASHMEETING' } };
      const response = await AXIOS.get( API.USERS + userId, config );
      CommonGlobalService.saveRequestToken( response, disp );
      if ( response.status === 202 ) { onAxiosWarning( response ); }
      if ( response.status === 200 ) { return response.data as UserUmanforge; }
      return null;
    } catch ( err ) {
      onAxiosError( err as AxiosError );
      return null;
    }
  }

  static getAllowedUsers(
    filters: UserFilter,
    token: string | null = null, dispatch?: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Users> {
    const tok = token ?? store.getState().token.value;
    const disp = dispatch ?? store.dispatch;
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, disp, onError ); };
    const filtersWithApp = { ...filters, appName: 'FLASHMEETING' } as unknown as UserFilter;
    return CommonUserService.getAllowedUsers( filtersWithApp, tok, disp, onAxiosWarning, onAxiosError );
  }

  static async getUsersOfSectors(
    sectorsId: string[],
    token: string | null = null, dispatch?: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UserUmanforge[]> {
    const tok = token ?? store.getState().token.value;
    const disp = dispatch ?? store.dispatch;
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, disp, onError ); };
    try {
      const config = { ...await CommonGlobalService.getHeaderRequest( tok ), params: { sectorsId, appName: 'FLASHMEETING' } };
      const response = await AXIOS.get( API.USERS + 'by_sectors/', config );
      CommonGlobalService.saveRequestToken( response, disp );
      if ( response.status === 202 ) { onAxiosWarning( response ); }
      if ( response.status === 200 ) { return response.data as UserUmanforge[]; }
      return [];
    } catch ( err ) {
      onAxiosError( err as AxiosError );
      return [];
    }
  }

}

